/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import { Alert, FlexCol, FlexRow, GuestCounterRow } from 'sarsaparilla';
import { AttendeeGroupsProps } from '../props';
import { COUNT_OVERALL_GUESTS, MAX_GROUP_COUNT } from '../constants';

export default class AttendeeInfoForm extends PureComponent {
    static defaultProps = {
        groups: {},
        isDisabled: false,
    };

    static propTypes = {
        groups: AttendeeGroupsProps,
        attendeeInfo: PropTypes.shape({
            adults: PropTypes.number,
            visitors: PropTypes.number,
        }),
        isRequired: PropTypes.bool,
        onChange: PropTypes.func,
        facilityId: PropTypes.string,
        isDisabled: PropTypes.bool,
        passType: PropTypes.object,
    };

    state = {
        currentGuestsCount: {},
        validOverallGuestCount: true,
    };

    componentDidMount() {
        this.props.onChange(this.props.attendeeInfo, true);

        this.setState(
            () => ({
                currentGuestsCount: this.getCounts(),
            }),
            () => {
                this.setState((prevState) => ({
                    validOverallGuestCount: !this.invalidGuestSelection(
                        prevState.currentGuestsCount
                    ),
                }));
            }
        );
    }

    getCounts = () => {
        const { groups = {}, attendeeInfo = {} } = this.props;
        const counts = {};
        if (groups === null || attendeeInfo === null) {
            return counts;
        }
        for (const key of Object.keys(groups)) {
            const min = this.getMin(key);
            counts[key] = attendeeInfo[key] ?? min;
        }
        return counts;
    };

    useOverallCount = () => {
        return COUNT_OVERALL_GUESTS.includes(this.props.facilityId);
    };

    handleChangeCount = (key, val) => {
        const min = this.getMin(key);
        const counts = this.getCounts();
        counts[key] = val >= min ? val : min;
        this.props.onChange(counts, true);
    };

    handleChangeCountOverall = (key, val) => {
        // Copy before updating so we never mutate state in place.
        const prev = isEmpty(this.state.currentGuestsCount)
            ? this.getCounts()
            : this.state.currentGuestsCount;
        const currentGuestsCount = { ...prev, [key]: val };

        const invalidSelection = this.invalidGuestSelection(currentGuestsCount);

        this.setState({
            currentGuestsCount,
            // eslint-disable-next-line react/no-unused-state
            validOverallGuestCount: !invalidSelection,
        });

        this.props.onChange(currentGuestsCount, !invalidSelection);
    };

    getMinOverall = () => {
        //We first look the facility in the overall counter array
        if (!this.useOverallCount()) {
            //Means the counter should not be overall, but for each guest
            return -1;
        }

        const { groups } = this.props;

        const keys = Object.keys(groups);

        //We only care for one of them
        const guest = groups[keys[0]];

        if (guest?.attendee_min) {
            return guest.attendee_min;
        }
        if (guest?.min_count) {
            return guest.min_count;
        }

        return guest && guest.is_required ? 1 : 0;
    };

    getMaxOverall = () => {
        //We first look the facility in the overall counter array
        if (!this.useOverallCount()) {
            //Means the counter should not be overall, but for each guest
            return -1;
        }

        const { groups } = this.props;
        const keys = Object.keys(groups);

        if (!isEmpty(keys)) {
            const guest = groups[keys[0]];

            if (guest?.attendee_max) {
                return guest.attendee_max;
            }
        }

        return MAX_GROUP_COUNT;
    };

    getOverallValue = (currentGuestsCount) => {
        if (isEmpty(currentGuestsCount)) return 2;
        let overallValue = 0;

        for (const key of Object.keys(currentGuestsCount)) {
            overallValue += currentGuestsCount[key];
        }

        return overallValue;
    };

    invalidGuestSelection = (currentGuestsCount) => {
        const overallValue = this.getOverallValue(currentGuestsCount);
        const minOverallValue = this.getMinOverall();
        const maxOverallValue = this.getMaxOverall();
        return overallValue < minOverallValue || overallValue > maxOverallValue;
    };

    getMin = (key) => {
        //When counting overall guests, the min will always be 0
        if (this.useOverallCount()) {
            return 0;
        }

        const { groups } = this.props;
        const group = groups[key] || {};

        if (group?.attendee_min) {
            return group.attendee_min;
        }
        if (group?.min_count) {
            return group.min_count;
        }
        return group && group.is_required ? 1 : 0;
    };

    render() {
        const { groups, isDisabled } = this.props;

        if (
            !groups ||
            (Object.keys(groups).length === 0 && groups.constructor === Object)
        ) {
            return null;
        }

        if (this.useOverallCount()) {
            return (
                <>
                    <FlexRow className="attendee-info-form">
                        {Object.values(groups).map((group) => {
                            const name = group.name || group.key;
                            const label = `Number of ${capitalize(name)}`;
                            return (
                                <FlexCol lg={6} key={name}>
                                    <div className="rec-form-item-wrap">
                                        <ul className="rec-guest-counter-wrap">
                                            <GuestCounterRow
                                                id={group.key}
                                                key={name}
                                                guestType={{
                                                    ...group,
                                                    key: name,
                                                    type: name,
                                                    label,
                                                }}
                                                isRequired={
                                                    group.is_required ||
                                                    this.props.isRequired
                                                }
                                                count={
                                                    this.state.currentGuestsCount[name]
                                                }
                                                changeCount={
                                                    this.handleChangeCountOverall
                                                }
                                                isDecreaseDisabled={isDisabled}
                                                maxTotalGuestsReached={isDisabled}
                                            />
                                        </ul>
                                    </div>
                                </FlexCol>
                            );
                        })}
                    </FlexRow>
                    {!this.state.validOverallGuestCount && (
                        <Alert type="info">
                            {`The total number of Adults and Children should be a minimum of ${this.getMinOverall()} and maximum of ${this.getMaxOverall()}`}
                        </Alert>
                    )}
                </>
            );
        }

        const counts = this.getCounts();
        return (
            <FlexRow className="attendee-info-form">
                {Object.values(groups).map((group) => {
                    const description =
                        group.name === 'adults' &&
                        this.props.passType?.features['is-commercial-pass']
                            ? 'Number of per person entrance fees for clients 16 and older'
                            : group.description;

                    const name = group.name || group.key;

                    const label =
                        group.name === 'adults' &&
                        this.props.passType?.features['is-commercial-pass']
                            ? 'Per Person Entrance Fees'
                            : `Number of ${capitalize(name)}`;
                    const minVal = this.getMin(name);
                    return (
                        <FlexCol lg={6} key={name}>
                            <div className="rec-form-item-wrap">
                                <ul className="rec-guest-counter-wrap">
                                    <GuestCounterRow
                                        id={group.key}
                                        key={name}
                                        guestType={{
                                            ...group,
                                            key: name,
                                            type: name,
                                            label,
                                            description,
                                        }}
                                        isRequired={
                                            group.is_required || this.props.isRequired
                                        }
                                        isDecreaseDisabled={
                                            (group.is_required &&
                                                counts[name] <= minVal) ||
                                            (name === 'adults' && counts[name] <= 1) ||
                                            isDisabled
                                        }
                                        count={counts[name]}
                                        maxTotalGuestsReached={
                                            group.attendee_max >= MAX_GROUP_COUNT ||
                                            group.max_count >= MAX_GROUP_COUNT ||
                                            counts[name] >= MAX_GROUP_COUNT ||
                                            isDisabled
                                        }
                                        changeCount={this.handleChangeCount}
                                    />
                                </ul>
                            </div>
                        </FlexCol>
                    );
                })}
            </FlexRow>
        );
    }
}
